import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SelfV2 } from 'src/app/core/alcmeon-context/models/self-v2.model';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { SiteV2 } from 'src/app/shared/models';

import { NavigationService } from '../services/navigation.service';
import { DropdownItems } from '../shared/models';

@Component({
  selector: 'app-account-selector',
  templateUrl: './accounts-selector.component.html',
})
export class AccountsSelectorComponent implements OnInit, OnDestroy {
  @Input() initialQueueId: string;
  @Output() sitesChange = new EventEmitter<string[]>();

  stateInboxNg = 'auth.alcmeon.inbox.list';
  sitesCtrl = new FormControl([]);

  sitesSelectorPanelClass = ['select-complex', 'inbox'];

  dropdownItems: DropdownItems;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private navigationService: NavigationService,
    private sitesService: SitesService,
    @Inject('$state') private $state: any,
    @Inject('DropdownItemsFactory') private dropdownItemsService: any,
    @Inject('SiteFilterHelperFactory') private siteFilterHelperService: any
  ) {}

  ngOnInit(): void {
    this.initializeAccountsSelector();
    this.initAccountsSelectorWithCache();

    this.subscriptions.add(
      this.sitesCtrl.valueChanges.subscribe((value: string[]) => {
        const { siteIds, siteGroupIds } = value.reduce(
          (result, key) => {
            if (/site\|/.test(key)) {
              result.siteIds.push(+key.replace('site|', ''));
            } else if (/siteGroup\|/.test(key)) {
              result.siteGroupIds.push(+key.replace('siteGroup|', ''));
            }
            return result;
          },
          { siteIds: [], siteGroupIds: [] }
        );

        this.navigationService.setSelectedSiteIdsAndUpdateAvailableTaskListConfigs(siteIds, siteGroupIds);
        this.$state.go(
          `${this.stateInboxNg}.tasks`,
          {
            queueId: this.initialQueueId,
            channelQueueId: 'all', // return to all, to avoid trying to stay on a site-specific queue
          },
          {
            reload: this.stateInboxNg,
          }
        );
      })
    );
  }

  private initializeAccountsSelector() {
    const loggedUser: SelfV2 = this.alcmeonContextService.self;

    const filteredSites: SiteV2[] = this.siteFilterHelperService.filterSitesThatHaveCapability(
      this.sitesService.sitesV2,
      loggedUser,
      CapabilityIdEnum.Answer
    );

    const sitesNotInGroup = filteredSites.filter(site => !site.site_group_id);

    const sortItems = (a, b) => {
      const result = a.type - b.type;
      if (!result) {
        return a.sortKey.localeCompare(b.sortKey);
      }
      return result;
    };

    this.dropdownItems = {
      siteGroupIds: null,
      siteIds: null,
      sites: null,
      sitesMap: null,
    };

    this.dropdownItems.siteIds = this.dropdownItemsService.buildSiteItems(sitesNotInGroup || []).sort(sortItems);
    this.dropdownItems.siteGroupIds = this.dropdownItemsService
      .buildSiteGroupItems(this.sitesService.siteGroupsV2 || [])
      .sort(sortItems);

    this.dropdownItems.sitesMap = new Map();
    this.dropdownItems.sites = [...this.dropdownItems.siteGroupIds, ...this.dropdownItems.siteIds].map((item: any) => {
      const result = { ...item, key: `${item.isGroup ? 'siteGroup' : 'site'}|${item.id}` };
      this.dropdownItems.sitesMap.set(result.key, result as Choice);
      return result;
    }) as Choice[];
  }

  private initAccountsSelectorWithCache() {
    const { siteIds, siteGroupIds } = this.navigationService.getSelectedSiteIds();
    const selectedSiteKeys = siteIds.map(id => `site|${id}`);
    const selectedSiteGroupKeys = siteGroupIds.map(id => `siteGroup|${id}`);
    const sitesKeys = [...selectedSiteGroupKeys, ...selectedSiteKeys];
    this.sitesCtrl.setValue(sitesKeys);
    this.sitesChange.emit(sitesKeys);
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
